import React, { useState, useRef } from 'react';
import { DSSheet, DSButton, DSInput, DSCheckbox } from '../design-system/primitives';
import { matchesName } from '../utils/signatureName';
import '../pages/AcknowledgeRules.css';

// Reading and signing one document, in a sheet.
//
// Used by the application step, where signing happens as part of a form that
// has not been submitted yet, so this component does not talk to the API - it
// hands the signature back and lets the caller decide what to do with it. The
// standalone signing screen has its own submit path for the same reason: there,
// signing is the whole point of the page.
//
// What both share is what a signature means: the document has been scrolled to
// the end, the box is ticked, and the name typed is the account holder's.
function DocumentSignSheet({
  title,
  label,
  content,
  agreeLabel,
  expectedName,
  currentUser,
  onSign,
  onClose
}) {
  const [signature, setSignature] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [readPct, setReadPct] = useState(0);
  const contentRef = useRef(null);

  const signatureMatches = matchesName(signature, currentUser);
  const atEnd = readPct > 0.985;
  const canSign = agreed && signature.trim() && signatureMatches && atEnd;

  const handleScroll = (event) => {
    const el = event.currentTarget;
    const max = el.scrollHeight - el.clientHeight;
    // Too short to scroll counts as read. Holding the button hostage to a
    // gesture that cannot happen is a dead end.
    setReadPct(max > 40 ? Math.min(1, el.scrollTop / max) : 1);
  };

  const skipToEnd = () => {
    const el = contentRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  };

  return (
    <DSSheet
      title={title}
      onClose={onClose}
      footer={
        <div style={{ display: 'flex', gap: '10px' }}>
          <DSButton appearance="outline" tone="neutral" onClick={onClose}>
            Close
          </DSButton>
          <DSButton
            appearance="solid"
            tone="primary"
            disabled={!canSign}
            onClick={() => onSign(signature.trim())}
          >
            {!atEnd ? 'Read to the end to sign' : `Sign the ${label.toLowerCase()}`}
          </DSButton>
        </div>
      }
    >
      <div
        ref={contentRef}
        onScroll={handleScroll}
        className="ack-document"
        dangerouslySetInnerHTML={{ __html: content }}
      />

      {!atEnd && (
        <div className="ack-readbar">
          <span className="ack-readbar__label">
            {readPct < 0.05 ? 'Read to the end to sign' : `${Math.round(readPct * 100)}% read`}
          </span>
          <DSButton appearance="solid" tone="primary" size="sm" onClick={skipToEnd}>
            Skip to the end
          </DSButton>
        </div>
      )}

      <div style={{ marginBottom: '16px' }}>
        <DSCheckbox
          id={`agree-${label}`}
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          label={agreeLabel}
        />
      </div>

      <DSInput
        label="Electronic signature"
        value={signature}
        placeholder={expectedName || 'Type your full name'}
        onChange={(e) => setSignature(e.target.value)}
        autoComplete="off"
        state={signature.trim() && !signatureMatches ? 'error' : 'inactive'}
        helpText={
          signature.trim() && !signatureMatches
            ? `Please sign with your full name, ${expectedName}.`
            : 'Typing your name is a legally binding electronic signature.'
        }
      />
    </DSSheet>
  );
}

export default DocumentSignSheet;
